// Deep Research configuration — every behavior is parameterized here.
// Modify defaults in one place; UI may override per-run via Guardrails.

import type { Guardrails } from "./types";

export interface ResearchConfig {
  planner: {
    minDepth: number;
    maxDepth: number;
    defaultDepth: number;
    branching: { min: number; max: number; default: number };
    plannerLatencyMs: { min: number; max: number };
  };
  swarm: {
    minAgents: number;
    maxAgents: number;
    defaultAgents: number;
    iterations: { min: number; max: number; default: number };
    earlyStopConfidence: number;
  };
  retrieval: {
    bm25TopK: number;
    denseTopK: number;
    graphTopK: number;
    rerankTopK: number;
    multiHopDepth: number;
    layerLatencyMs: Record<"bm25" | "dense" | "graph" | "rerank", { min: number; max: number }>;
  };
  critic: {
    refutationRounds: number;
    weakClaimThreshold: number;
    rejectClaimThreshold: number;
  };
  selfConsistency: {
    samples: number;
    minAgreeRatio: number;
  };
  cost: {
    // simulated $ per 1k tokens for cost accounting (not wired to a real meter)
    perKTokens: number;
    perToolCall: number;
  };
}

export const DEFAULT_CONFIG: ResearchConfig = {
  planner: {
    minDepth: 2,
    maxDepth: 5,
    defaultDepth: 3,
    branching: { min: 2, max: 4, default: 3 },
    plannerLatencyMs: { min: 380, max: 760 },
  },
  swarm: {
    minAgents: 2,
    maxAgents: 32,
    defaultAgents: 8,
    iterations: { min: 1, max: 5, default: 5 },
    earlyStopConfidence: 0.85,
  },
  retrieval: {
    bm25TopK: 50,
    denseTopK: 50,
    graphTopK: 20,
    rerankTopK: 15,
    multiHopDepth: 3,
    layerLatencyMs: {
      bm25:   { min: 80,  max: 220 },
      dense:  { min: 140, max: 420 },
      graph:  { min: 110, max: 320 },
      rerank: { min: 180, max: 500 },
    },
  },
  critic: {
    refutationRounds: 2,
    weakClaimThreshold: 0.55,
    rejectClaimThreshold: 0.30,
  },
  selfConsistency: {
    samples: 5,
    minAgreeRatio: 0.6,
  },
  cost: {
    perKTokens: 0.0035,
    perToolCall: 0.0008,
  },
};

export const DEFAULT_GUARDRAILS: Guardrails = {
  maxTokens: 250_000,
  maxTimeSec: 180,
  maxCostUsd: 1.5,
};

export const SEED_DEMO_QUERY =
  "What are the most promising approaches to solving alignment in frontier LLMs as of 2025?";
